/**
 * A realistic sample of the /api/admin/rds-health/ payload.
 *
 * Purpose: let an operator read the dashboard BEFORE the live RDS
 * answers (or when it is down), and let them see it in every state that
 * matters. The payload is operational metadata only -- flag names, table
 * names, counts, timestamps. No PHI, no real tenant data.
 *
 * The shape is exactly the shape buildViewModel() expects, so the layout
 * cannot tell a sample from a live payload.
 */

// "Now" is the moment the module loaded, so the sample's ages ("45 s ago")
// read like a live payload instead of a two-day-old one.
const NOW = new Date().toISOString();

function ago(seconds) {
  return new Date(Date.parse(NOW) - seconds * 1000).toISOString();
}

function flag(overrides) {
  return {
    enabled: true,
    effective: true,
    reason: null,
    table: "patients_with_ehrid",
    note: null,
    updated_at: ago(600),
    ...overrides,
  };
}

const CUTOVER_FLAGS = {
  overlay_patients_with_ehrid: flag({ table: "patients_with_ehrid" }),
  overlay_notes_c: flag({ table: "notes-c" }),
  overlay_patients: flag({ table: "patients" }),
  overlay_SMS: flag({ table: "SMS", effective: false, reason: "mirror stale for SMS (1420s > 900s)" }),
  write_patients: flag({ table: "patients" }),
  write_visit_template: flag({ table: "notes-c" }),
  patient_list: flag({ table: "patients_with_ehrid" }),
  patient_suggest: flag({ table: "patients_with_ehrid" }),
  patient_table: flag({ table: "patients_with_ehrid" }),
  roster_visits: flag({ table: "notes-c" }),
  latest_visit: flag({ table: "notes-c" }),
  storage_files: flag({ table: "bucket:files", enabled: true, effective: true }),
  storage_audio: flag({ table: "bucket:audio", enabled: true, effective: false, reason: "bucket never synced" }),
  storage_copilot: flag({ table: "bucket:copilot", enabled: false, effective: false }),
};

const BASE = {
  liveness: { reachable: true, http_status: 200 },
  flags: { RDS_PHI_ONLY: false, RDS_SHADOW_READ_NOTES_C: true },
  shadow_reads: {
    patient_list: [
      { at: ago(120), tenant: "public", mode: "cutover", served: "rds", outcome: "ok", sample_size: 240, matched: 240, mismatched: 0, missing: 0 },
      { at: ago(600), tenant: "clinic-a", mode: "cutover", served: "supabase_fallback", reason: "rds_empty", outcome: "rds_empty" },
    ],
    overlay_notes_c: [
      { at: ago(90), tenant: "public", mode: "cutover", served: "rds", outcome: "ok", sample_size: 58, matched: 58, mismatched: 0, missing: 0, masked: 0 },
    ],
    storage_files: [
      { at: ago(45), tenant: "public", mode: "cutover", served: "rds", outcome: "ok", sample_size: 1 },
    ],
  },
  readiness: {
    checked: true,
    status: "healthy",
    checked_at: ago(30),
    version: "rds-2026.10",
    database: { connected: true, latency_ms: 12 },
    security: { auth_enforced: true, phi_only: false },
    access_mirror: { last_synced_at: ago(240), age_seconds: 240 },
    audit_pipeline: { last_event_at: ago(75), age_seconds: 75 },
    auth_columns_missing: [],
    data_mirror: {
      last_synced_at: ago(180),
      age_seconds: 180,
      tables: 12,
      max_age_seconds: 900,
      failed: [],
      by_table: {
        patients_with_ehrid: { schemas: 14, max_age_seconds: 180, failed: [], never_synced: [] },
        "notes-c": { schemas: 14, max_age_seconds: 210, failed: [], never_synced: [] },
        patients: { schemas: 14, max_age_seconds: 240, failed: [], never_synced: [] },
        SMS: { schemas: 14, max_age_seconds: 1420, failed: [], never_synced: [] },
      },
      last_run: {
        ok: true,
        trigger: "scheduler",
        finished_at: ago(180),
        summary: { ok: 12, tables: 12, changed: 3, orphans: 0 },
      },
    },
    storage_mirror: {
      by_bucket: {
        files: { rds_objects: 412, source_objects: 412, total_bytes: 1874422016, last_synced_at: ago(300), ok: true },
        audio: { rds_objects: 88, source_objects: 91, total_bytes: 9123348800, last_synced_at: ago(1800), ok: false, error: "3 files could not be downloaded" },
        copilot: { rds_objects: 0, source_objects: 12, total_bytes: 4194304, last_synced_at: null, ok: false },
      },
    },
    source_mode: { mode: "mirror", since: ago(864000), catchup: true, pinned: false, note: null },
    cutover: { kill_switch: false, flags: CUTOVER_FLAGS },
  },
};

/** The four states an operator has to be able to read at a glance. */
export const SAMPLE_STATES = [
  {
    id: "partial",
    name: "Mirror mode, part-migrated",
    blurb: "Supabase is still the source of truth. Some tables already serve their PHI from RDS.",
    build: { REACT_APP_RDS_CUTOVER: "1" },
    payload: BASE,
  },
  {
    id: "independent",
    name: "RDS independent",
    blurb: "RDS owns the PHI rows; Supabase serves sign-in and non-PHI columns.",
    build: { REACT_APP_RDS_CUTOVER: "1" },
    payload: {
      ...BASE,
      readiness: {
        ...BASE.readiness,
        source_mode: { mode: "independent", since: ago(7200), catchup: true, pinned: false, note: "went independent this morning" },
        data_mirror: { ...BASE.readiness.data_mirror, suspended: true },
        cutover: {
          kill_switch: false,
          flags: {
            ...CUTOVER_FLAGS,
            overlay_patients_with_ehrid: flag({ table: "patients_with_ehrid", forced: true }),
            overlay_notes_c: flag({ table: "notes-c", forced: true }),
            overlay_patients: flag({ table: "patients", forced: true }),
            overlay_SMS: flag({ table: "SMS", forced: true, effective: false, reason: "mirror stale for SMS (1420s > 900s)" }),
            patient_list: flag({ table: "patients_with_ehrid", enabled: false, effective: false, forced: true, reason: "independent: served through the PHI overlay" }),
            patient_suggest: flag({ table: "patients_with_ehrid", enabled: false, effective: false, forced: true, reason: "independent: served through the PHI overlay" }),
          },
        },
      },
    },
  },
  {
    id: "build-off",
    name: "This build never asks RDS",
    blurb: "RDS_API_URL is not set, so the frontend reads Supabase whatever the flags say.",
    build: { REACT_APP_RDS_CUTOVER: "" },
    payload: BASE,
  },
  {
    id: "down",
    name: "RDS unreachable",
    blurb: "The health endpoint did not answer, so every read falls back to Supabase.",
    build: { REACT_APP_RDS_CUTOVER: "1" },
    payload: {
      ...BASE,
      liveness: { reachable: false, http_status: null },
      readiness: { ...BASE.readiness, checked: false, status: "unavailable", reason: "no answer from RDS" },
    },
  },
];

/**
 * A sample of RDS's GET /admin/activity aggregate (what RDS served, from its
 * audit log), shaped exactly like the real one: a 5-minute window in 6-second
 * slots, totals per family, distinct viewers and the newest events. Names,
 * counts and timestamps only.
 */
export function sampleActivityPayload({ busy = true, mode = "mirror" } = {}) {
  const windowSeconds = 300;
  const slot = 6;
  const nowSec = Math.floor(Date.parse(NOW) / 1000);
  const last = Math.floor(nowSec / slot) * slot;
  const timeline = [];
  let events = 0;
  let reads = 0;
  let writes = 0;
  for (let i = 49; i >= 0; i -= 1) {
    const r = busy ? (i * 7) % 5 : 0; // a deterministic ripple, 0..4 reads a slot
    const w = busy && i % 9 === 0 ? 1 : 0;
    if (r + w === 0) continue;
    timeline.push({ t: last - i * slot, read: r, write: w, delete: 0, miss: 0, admin: 0, events: r + w });
    events += r + w;
    reads += r;
    writes += w;
  }
  const tail = busy ? [
    { id: 1000, ts: ago(8), age_seconds: 8, viewer: "a1b2c3d4", kind: "table", schema: "public", name: "patients_with_ehrid", target: "table:public.patients_with_ehrid", mode: "query", family: "read" },
    { id: 999, ts: ago(21), age_seconds: 21, viewer: "e5f6a7b8", kind: "table", schema: "clinic-a", name: "notes-c", target: "table:clinic-a.notes-c", mode: "list", family: "read" },
    { id: 998, ts: ago(64), age_seconds: 64, viewer: "a1b2c3d4", kind: "table", schema: "public", name: "patients", target: "table:public.patients", mode: "write", family: "write" },
    { id: 997, ts: ago(140), age_seconds: 140, viewer: "c9d0e1f2", kind: "bucket", schema: null, name: "files", target: "bucket:files", mode: "storage-download", family: "read" },
  ] : [];
  return {
    now: NOW,
    window_seconds: windowSeconds,
    slot_seconds: slot,
    cursor: 1000,
    nodes: [],
    timeline,
    tail,
    totals: {
      read: reads, write: writes, delete: 0, miss: 0, admin: 0, events,
      viewers: busy ? 3 : 0, active_tables: busy ? 3 : 0, active_buckets: busy ? 1 : 0,
    },
    source_mode: { mode },
    jobs: {},
  };
}

/**
 * The payload for a sample state, with the build flag forced on or off, and
 * the matching RDS activity sample (none when RDS is down -- the real call
 * fails then too).
 */
export function samplePayload(stateId) {
  const state = SAMPLE_STATES.find((s) => s.id === stateId) || SAMPLE_STATES[0];
  const activity = state.id === "down"
    ? null
    : sampleActivityPayload({
      busy: state.id !== "build-off",
      mode: state.id === "independent" ? "independent" : "mirror",
    });
  return {
    payload: state.payload,
    buildOn: state.build.REACT_APP_RDS_CUTOVER === "1",
    activity,
  };
}
